import React, { Suspense, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { PERSONALIZADOS } from './registry';
import { GlobalContext } from '../lib/GlobalVar';
import { ConsultaInternas } from '../lib/api';
import { podeAcessarTela, ehRestrito } from '../lib/acessoTela';
import './personalizados-largura.css';

// Resolve o slug no registry e renderiza o personalizado isolado por ErrorBoundary.
// Antes de renderizar, aplica o controle de acesso da entrada de Paginas Internas
// correspondente (modo_acesso/setores) — TI/Administrador sempre liberados; entrada
// inexistente = sem restricao configurada (libera).
export default function PersonalizadoHost({ slug: slugProp, ativo = true }) {
    const params = useParams();
    // Dentro de uma aba (AbasPersonalizadas) o slug vem por prop: a tela segue montada mesmo quando a rota e de outra aba.
    const slug = slugProp || params.slug;
    const navigate = useNavigate();
    const { user } = useContext(GlobalContext);
    const entry = PERSONALIZADOS[slug];
    const [acesso, setAcesso] = useState('checando'); // checando | ok | negado

    // Tela personalizada ocupa a largura toda (personalizados-largura.css): o teto de
    // 1480px do `.page` global aperta tabela larga, pivo e quadro. A marca vai no
    // <body> em vez de embrulhar o componente numa div porque ha CSS que depende de
    // filho direto (ex.: `.chat-direto .cd-body > .page.pd`, do Perguntar aos Dados
    // dentro do chat) — embrulhar quebraria esses seletores. Sai da rota, sai a marca.
    useEffect(() => {
        if (!ativo) return undefined;   // aba escondida nao mexe no <body>
        document.body.classList.add('pz-largura-total');
        return () => document.body.classList.remove('pz-largura-total');
    }, [ativo]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const internas = await ConsultaInternas({});
                const lista = Array.isArray(internas) ? internas : (internas.telas || []);
                const rota = `/personalizados/${slug}`;
                const tela = lista.find(t => t.rota === rota || t.slug === slug);
                if (!alive) return;
                // pagina sem entrada de menu: abre, menos para conta restrita (so entra no que foi liberado a ela pelo nome)
                setAcesso((tela ? podeAcessarTela(user, tela) : !ehRestrito(user)) ? 'ok' : 'negado');
            } catch {
                if (alive) setAcesso(ehRestrito(user) ? 'negado' : 'ok');
            }
        })();
        return () => { alive = false; };
    }, [slug, user]);

    if (!entry) {
        return (
            <div className="page">
                <div className="page-head">
                    <div>
                        <div className="eyebrow"><span className="num">!</span> Personalizado</div>
                        <h1 className="page-title">Tela nao encontrada</h1>
                        <p className="page-sub">Nenhum personalizado registrado para "{slug}".</p>
                    </div>
                    <button className="btn ghost sm" onClick={() => navigate('/Internas')}>&larr; Paginas Internas</button>
                </div>
            </div>
        );
    }

    if (acesso === 'checando') {
        return <div className="page muted" style={{ padding: 24 }}>Verificando acesso…</div>;
    }
    if (acesso === 'negado') {
        return (
            <div className="page">
                <div className="page-head">
                    <div>
                        <div className="eyebrow"><span className="num">!</span> Acesso negado</div>
                        <h1 className="page-title">Sem permissão</h1>
                        <p className="page-sub">Esta tela não está liberada para o seu setor. Procure o TI se precisar de acesso.</p>
                    </div>
                </div>
            </div>
        );
    }

    const Componente = entry.componente;
    return (
        <ErrorBoundary titulo={entry.titulo}>
            <Suspense fallback={<div className="page muted" style={{ padding: 24 }}>Carregando {entry.titulo}…</div>}>
                <Componente />
            </Suspense>
        </ErrorBoundary>
    );
}
